import { FORM_VALIDATOR_TYPES } from '../constants';
import { IStudentFormData, STUDENT_STATUS } from '../interface/studentModel';
import { tr } from '../i18n/i18n';

export const bloodTypes = [
  { label: 'A+', code: 'A+' },
  { label: 'A-', code: 'A-' },
  { label: 'B+', code: 'B+' },
  { label: 'B-', code: 'B-' },
  { label: 'AB+', code: 'AB+' },
  { label: 'AB-', code: 'AB-' },
  { label: 'O+', code: 'O+' },
  { label: 'O-', code: 'O-' },
];

export const genderOptions = [
  { label: tr('ui.male', 'Male'), code: 'Male' },
  { label: tr('ui.female', 'Female'), code: 'Female' },
];

/**
 * Display labels for every student lifecycle status.
 */
export const STUDENT_STATUS_LABELS: Record<STUDENT_STATUS, string> = {
  [STUDENT_STATUS.ACTIVE]: 'Active',
  [STUDENT_STATUS.INACTIVE]: 'Inactive',
  [STUDENT_STATUS.GRADUATED]: 'Graduated',
  [STUDENT_STATUS.DROP_OFF]: 'Drop Off',
};

/**
 * Text color classes per student lifecycle status (used by list-page status columns).
 */
export const STUDENT_STATUS_TEXT_CLASSES: Record<STUDENT_STATUS, string> = {
  [STUDENT_STATUS.ACTIVE]: 'text-green-600',
  [STUDENT_STATUS.INACTIVE]: 'text-purple-600',
  [STUDENT_STATUS.GRADUATED]: 'text-blue-600',
  [STUDENT_STATUS.DROP_OFF]: 'text-orange-600',
};

/**
 * Terminal "school leaver" statuses. These are read-only in profile forms and
 * can only be reached through the re-enrollment GRADUATE/DROP_OFF transition.
 */
export const TERMINAL_STUDENT_STATUSES: STUDENT_STATUS[] = [
  STUDENT_STATUS.GRADUATED,
  STUDENT_STATUS.DROP_OFF,
];

export const isTerminalStudentStatus = (status?: STUDENT_STATUS | string | null): boolean =>
  TERMINAL_STUDENT_STATUSES.includes(status as STUDENT_STATUS);

/**
 * Statuses an admin can set from student forms (terminal states are excluded —
 * they are set exclusively by the re-enrollment transition flow).
 */
export const studentStatusOptions = [
  { label: STUDENT_STATUS_LABELS[STUDENT_STATUS.ACTIVE], code: STUDENT_STATUS.ACTIVE },
  { label: STUDENT_STATUS_LABELS[STUDENT_STATUS.INACTIVE], code: STUDENT_STATUS.INACTIVE },
];

export const STUDENT_FORM_INIT: IStudentFormData = {
  status: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.OBJECT,
    disable: false,
  },
  studentId: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: true, // Always read-only
  },
  firstName: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  lastName: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  displayName: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  email: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.EMAIL,
    disable: false,
  },
  curriculum: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  grade: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  requestedGrade: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  classroom: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  gender: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.OBJECT,
    disable: false,
  },
  dob: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.DATE,
    disable: false,
  },
  address: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  blood: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.OBJECT,
    disable: false,
  },
  admissionDate: {
    value: null,
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.DATE,
    disable: false,
  },
  admissionNo: {
    value: '',
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  medication: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  allergies: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  emergencyName: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  emergencyContact: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
};
